import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

export interface LoadingProps {
  /** Display variant - 'overlay' for full-screen blocking, 'inline' for in-content */
  variant?: 'overlay' | 'inline';

  /** Size of the spinner - 'small' (16), 'medium' (24), 'large' (40), or custom number */
  size?: 'small' | 'medium' | 'large' | number;

  /** Text to display below spinner */
  message?: string;

  /** Secondary message below main message */
  subMessage?: string;

  /** Minimum height for container (inline variant only) */
  minHeight?: string | number;
}

const sizeMap = {
  small: 16,
  medium: 24,
  large: 40,
};

/**
 * Unified loading component with overlay and inline variants.
 * Uses MUI CircularProgress for consistent styling across the application.
 */
export const Loading: React.FC<LoadingProps> = ({
  variant = 'overlay',
  size = 'large',
  message = 'Loading...',
  subMessage,
  minHeight = '400px',
}) => {
  const spinnerSize = typeof size === 'number' ? size : sizeMap[size];

  if (variant === 'overlay') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 9999,
        }}
      >
        <CircularProgress size={spinnerSize} sx={{ mb: 2 }} />
        {message && <Typography variant="h6">{message}</Typography>}
        {subMessage && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {subMessage}
          </Typography>
        )}
      </Box>
    );
  }

  // Inline variant
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight={minHeight}
      flexDirection="column"
      gap={2}
    >
      <CircularProgress size={spinnerSize} />
      {message && (
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      )}
      {subMessage && (
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          {subMessage}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Pre-configured full-page overlay loading component.
 * Use this for page-level loading states.
 */
export const PageLoading: React.FC<{ message?: string; subMessage?: string }> = ({
  message = 'Loading...',
  subMessage,
}) => <Loading variant="overlay" message={message} subMessage={subMessage} />;

/**
 * Pre-configured inline loading component for grids and content areas.
 * Use this for data grid or section loading states.
 */
export const GridLoading: React.FC<{ message?: string; minHeight?: string | number }> = ({
  message = 'Loading...',
  minHeight = '300px',
}) => <Loading variant="inline" message={message} minHeight={minHeight} />;

/**
 * Authentication loading component for MSAL authentication flows.
 * This component accepts and ignores MSAL context props to satisfy the
 * MsalAuthenticationTemplate loadingComponent prop type requirements.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AuthLoading: React.FC<any> = () => <Loading variant="inline" message="Loading..." minHeight="400px" />;

export default Loading;
